import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { type Application, apis, applications } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Empty,
  Failure,
  Field,
  FormRow,
  Hint,
  Loading,
  Mono,
  PageHeader,
  Section,
  Tag,
  Tags,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// Applications
//
// The clients that request tokens. Each declares which APIs it may name as `resource`, and the
// token endpoint enforces it — which is what makes a client_id mean something rather than being a
// string echoed into the token.
// ---

const TYPES = [
  { value: "spa", label: "Single-page app", confidential: false, redirects: true },
  { value: "native", label: "Native / mobile", confidential: false, redirects: true },
  { value: "regular_web", label: "Regular web app", confidential: false, redirects: true },
  { value: "m2m", label: "Machine to machine", confidential: true, redirects: false },
  { value: "service", label: "Backend service", confidential: true, redirects: false },
];

/** One URL per line, which is how people paste them. Blank lines are not entries. */
function parseUris(text: string): string[] {
  return text
    .split(/[\r\n]+/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export function Applications({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const list = useLoad(() => applications.list(tenant, token), [tenant, token]);
  const apiList = useLoad(() => apis.list(tenant, token), [tenant, token]);
  const action = useAction(() => list.reload());
  const [secret, setSecret] = useState<{ clientId: string; value: string } | null>(null);
  const [draft, setDraft] = useState({
    clientId: "",
    name: "",
    type: "spa",
    allowedAudiences: [] as string[],
    redirectUris: "",
  });
  // Which row's return URLs are open for editing. One at a time: they are rarely changed, and a
  // row that is always an input field reads as a form rather than a record.
  const [editing, setEditing] = useState<{ clientId: string; value: string } | null>(null);

  const available = apiList.data ?? [];
  const canManage = scopes.includes("application:manage");
  const confidential = TYPES.find((type) => type.value === draft.type)?.confidential === true;
  // A machine client has nowhere to send anybody, so the field would be an invitation to fill in
  // something that can never apply.
  const redirects = TYPES.find((type) => type.value === draft.type)?.redirects === true;

  return (
    <>
      <PageHeader title="Applications" />

      {secret !== null && (
        <Section tone="warning" title={`Client secret for ${secret.clientId}`}>
          <Mono
            sx={{
              display: "block",
              wordBreak: "break-all",
              p: 3,
              bgcolor: tokens.surface.cardSunken,
              borderRadius: `${tokens.radius.md}px`,
            }}
          >
            {secret.value}
          </Mono>
          <Alert severity="warning" sx={{ mt: 3 }}>
            Shown once. Only a hash is stored, so this cannot be recovered — copy it now.
          </Alert>
        </Section>
      )}

      {canManage && (
        <Section title="Register an application">
          <FormRow>
            <Field label="Client id" sx={{ flex: "1 1 170px" }}>
              <TextField
                size="small"
                value={draft.clientId}
                onChange={(event) => setDraft({ ...draft, clientId: event.target.value })}
                placeholder="acme-web"
              />
            </Field>
            <Field label="Name" sx={{ flex: "1 1 170px" }}>
              <TextField
                size="small"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Defaults to the client id"
              />
            </Field>
            <Field label="Type" sx={{ flex: "1 1 190px" }}>
              <Select
                size="small"
                value={draft.type}
                onChange={(event) => setDraft({ ...draft, type: event.target.value })}
              >
                {TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </Field>
          </FormRow>

          <Box sx={{ typography: "overline", color: tokens.ink.muted, mt: 5, mb: 1 }}>
            APIs this application may request
          </Box>
          {available.length === 0 ? (
            <Hint sx={{ mt: 0 }}>Register an API first.</Hint>
          ) : (
            <Stack>
              {available.map((api) => (
                <FormControlLabel
                  key={api.resource}
                  control={
                    <Checkbox
                      size="small"
                      checked={draft.allowedAudiences.includes(api.resource)}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          allowedAudiences: event.target.checked
                            ? [...draft.allowedAudiences, api.resource]
                            : draft.allowedAudiences.filter((each) => each !== api.resource),
                        })
                      }
                    />
                  }
                  label={<Mono>{api.resource}</Mono>}
                />
              ))}
            </Stack>
          )}
          {redirects && (
            <>
              <Box sx={{ typography: "overline", color: tokens.ink.muted, mt: 5, mb: 1 }}>
                Return URLs
              </Box>
              <TextField
                multiline
                minRows={2}
                fullWidth
                size="small"
                value={draft.redirectUris}
                onChange={(event) => setDraft({ ...draft, redirectUris: event.target.value })}
                placeholder={"https://app.example.com/callback\nhttps://app.example.com/welcome"}
                spellCheck={false}
                InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: 12.5 } }}
              />
              <Hint sx={{ mb: 0 }}>
                One per line. OAuth redirect targets — and also what lets an invitation land on this
                application's own page, and a password reset started here be sent in this tenant's
                words.
              </Hint>
            </>
          )}
          <Hint>
            {confidential
              ? "A secret is issued once at registration for this type."
              : "A public client receives no secret: one shipped inside an app or a browser bundle is a published secret."}
          </Hint>
          <Button
            variant="contained"
            sx={{ mt: 4 }}
            disabled={draft.clientId === "" || action.busy}
            onClick={() =>
              action.run(async () => {
                const created = await applications.create(tenant, token, {
                  ...draft,
                  name: draft.name || draft.clientId,
                  redirectUris: parseUris(draft.redirectUris),
                });
                if (created.clientSecret !== undefined) {
                  setSecret({ clientId: created.clientId, value: created.clientSecret });
                }
                setDraft({
                  clientId: "",
                  name: "",
                  type: "spa",
                  allowedAudiences: [],
                  redirectUris: "",
                });
              })
            }
          >
            Register application
          </Button>
          <Failure error={action.error} />
        </Section>
      )}

      <Section flush title={`Registered · ${list.data?.length ?? 0}`}>
        <Failure error={list.error} />
        {list.pending ? (
          <Loading what="applications" />
        ) : list.data?.length === 0 ? (
          <Empty>
            None yet. Until an application is registered, only the console may request tokens.
          </Empty>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Allowed APIs</TableCell>
                <TableCell>Return URLs</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data?.map((app: Application) => (
                <TableRow key={app.clientId} hover>
                  <TableCell>
                    <Mono>{app.clientId}</Mono>
                  </TableCell>
                  <TableCell>
                    <Tags>
                      <Tag label={app.type} />
                      {app.hasSecret && <Tag label="secret" />}
                    </Tags>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      label={app.status}
                      tone={app.status === "active" ? "success" : "neutral"}
                    />
                  </TableCell>
                  <TableCell>
                    {app.allowedAudiences.length === 0 ? (
                      <Box component="span" sx={{ color: tokens.ink.muted }}>
                        None — can request nothing
                      </Box>
                    ) : (
                      <Tags>
                        {app.allowedAudiences.map((audience) => (
                          <Tag key={audience} label={audience.replace("https://", "")} />
                        ))}
                      </Tags>
                    )}
                  </TableCell>
                  <TableCell>
                    {editing?.clientId === app.clientId ? (
                      <Stack spacing={2} sx={{ py: 2 }}>
                        <TextField
                          multiline
                          minRows={2}
                          size="small"
                          autoFocus
                          value={editing.value}
                          onChange={(event) =>
                            setEditing({ clientId: app.clientId, value: event.target.value })
                          }
                          spellCheck={false}
                          InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: 12 } }}
                        />
                        <FormRow sx={{ gap: 2 }}>
                          <Button size="small" variant="text" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={action.busy}
                            onClick={() =>
                              action.run(async () => {
                                await applications.setRedirectUris(
                                  tenant,
                                  token,
                                  app.clientId,
                                  parseUris(editing.value),
                                );
                                setEditing(null);
                              })
                            }
                          >
                            Save
                          </Button>
                        </FormRow>
                      </Stack>
                    ) : app.redirectUris.length === 0 ? (
                      <Box component="span" sx={{ color: tokens.ink.muted }}>
                        None
                      </Box>
                    ) : (
                      <Tags>
                        {app.redirectUris.map((uri) => (
                          <Tag key={uri} label={uri} />
                        ))}
                      </Tags>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canManage &&
                      TYPES.find((type) => type.value === app.type)?.redirects === true &&
                      editing?.clientId !== app.clientId && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            setEditing({
                              clientId: app.clientId,
                              value: app.redirectUris.join("\n"),
                            })
                          }
                        >
                          URLs
                        </Button>
                      )}
                    {canManage && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          action.run(() =>
                            applications.setStatus(
                              tenant,
                              token,
                              app.clientId,
                              app.status === "active" ? "disabled" : "active",
                            ),
                          )
                        }
                      >
                        {app.status === "active" ? "Disable" : "Enable"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
}
